import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import React, { useEffect, useState } from 'react';
import {
  useGetUserByIdQuery,
  useLazyGetTransactionForUserQuery,
  useStaffForgotPasswordMutation,
  useDeactivateUserMutation,
  useReactivateUserMutation,
  useUpdateUserMutation,
} from '../../apis/konektApi';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import Loader from '../../components/Loader';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { IExpensesTransaction, IIncomesTransaction } from '../../apis/models/transactions';
import MainCard from '../../components/MainCard';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import IncomeTransactionHistory from './IncomeTransactionHistory';
import ExpenseTransactionHistory from './ExpenseTransactionHistory';
import StaffActivityFeed from './StaffActivityFeed';
import { dispatch } from '../../store';
import { openSnackbar } from '../../store/reducers/snackbar';

const UserDetailsPage = () => {
  const incomeFeeTypes = useSelector((state: RootState) => {
    return state.feeTypes.other.concat(state.feeTypes.student);
  });
  const expensesFeeTypes = useSelector((state: RootState) => {
    return state.feeTypes.expenses;
  });
  const tenantId = useSelector((state: RootState) => state.login.tenantId ?? '');
  const [getTransactionForUser, userTransactionResult] = useLazyGetTransactionForUserQuery();
  const [staffForgotPassword, { isLoading: resetLoading }] = useStaffForgotPasswordMutation();
  const [deactivateUser, { isLoading: deactivateLoading }] = useDeactivateUserMutation();
  const [reactivateUser, { isLoading: reactivateLoading }] = useReactivateUserMutation();
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs(new Date()));
  const { id } = useParams();
  const { t } = useTranslation();

  const onGetTransactionForUser = async () => {
    if (selectedDate) {
      const date = new Date(selectedDate?.toISOString()).toISOString();
      const userId = id || '';
      await getTransactionForUser({ userId, date });
    }
  };

  const onSendPasswordReset = async () => {
    if (!data?.username) return;
    try {
      await staffForgotPassword({ username: data.username, companyName: tenantId }).unwrap();
      dispatch(
        openSnackbar({
          open: true,
          message: t('staff.reset_sent_success', { email: data.email }),
          variant: 'alert',
          alert: { color: 'success' },
          close: false,
        })
      );
    } catch {
      dispatch(
        openSnackbar({
          open: true,
          message: t('errors.generic_error'),
          variant: 'alert',
          alert: { color: 'error' },
          close: false,
        })
      );
    }
  };

  const onToggleActive = async () => {
    if (!id) return;
    const isActive = data?.isActive !== false;
    try {
      if (isActive) {
        await deactivateUser({ id }).unwrap();
      } else {
        await reactivateUser({ id }).unwrap();
      }
      dispatch(
        openSnackbar({
          open: true,
          message: isActive
            ? t('staff.deactivated_success', { name: data?.names })
            : t('staff.reactivated_success', { name: data?.names }),
          variant: 'alert',
          alert: { color: isActive ? 'warning' : 'success' },
          close: false,
        })
      );
    } catch {
      dispatch(
        openSnackbar({
          open: true,
          message: t('errors.generic_error'),
          variant: 'alert',
          alert: { color: 'error' },
          close: false,
        })
      );
    }
  };

  const [country, setCountryCode] = useState(40);
  const handleCountryCodeChange = (event) => {
    setCountryCode(event.target.value);
  };
  const [updateUser, { isLoading: saveLoading }] = useUpdateUserMutation();
  const [editEmail, setEditEmail] = useState('');
  const [editCellNumber, setEditCellNumber] = useState('');
  const { data, error, isLoading } = useGetUserByIdQuery({ id: id || '' });

  useEffect(() => {
    if (data) {
      setEditEmail(data.email || '');
      setEditCellNumber(data.cellNumber || '');
    }
  }, [data]);
  if (isLoading) {
    return <Loader />;
  } else if (error) {
    return (
      <Box>
        <Typography>{t('errors.generic_error')}</Typography>
      </Box>
    );
  }
  let incomesColumnsData: Array<IIncomesTransaction> = [];
  let expensesColumnsData: Array<IExpensesTransaction> = [];
  if (userTransactionResult && userTransactionResult?.data?.incomes.length) {
    incomesColumnsData = userTransactionResult?.data?.incomes.map((row) => {
      const translatedValue = incomeFeeTypes.find((item) => {
        return item && item.enum_key && item.enum_key === row.feeType;
      });
      return {
        ...row,
        feeType: translatedValue?.value_fr || 'Compte non trouvé',
      };
    });
  }
  if (userTransactionResult && userTransactionResult?.data?.expenses.length) {
    expensesColumnsData = userTransactionResult?.data?.expenses.map((row) => {
      const translatedValue = expensesFeeTypes.find((item) => item.enum_key === row.feeCode);
      return { ...row, feeCode: translatedValue?.value_fr || 'Compte non trouvé' };
    });
  }
  const getIncomesTotal = () => {
    let total = 0;
    if (userTransactionResult && userTransactionResult?.data?.incomes.length) {
      total = userTransactionResult?.data?.incomes.reduce((a, b) => a + b.fee_amount, 0);
    }
    return total.toFixed(2);
  };
  const getExpensesTotal = () => {
    let total = 0;
    if (userTransactionResult && userTransactionResult?.data?.expenses.length) {
      total = userTransactionResult?.data?.expenses.reduce((a, b) => a + b.fee_amount, 0);
    }
    return total.toFixed(2);
  };
  return (
    <MainCard title={t('titles.user_details')}>
      {data ? (
        <Grid container spacing={3}>
          <Grid item lg={4} md={6} xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography color="textPrimary" gutterBottom variant="h3">
                    {data.names}
                  </Typography>
                  <Typography color="textPrimary" gutterBottom variant="h5">
                    {data.role}
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    {data.email}
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    {data.cellNumber}
                  </Typography>
                  <Box mt={1}>
                    <Chip
                      label={
                        data.isActive !== false
                          ? t('staff.status_active')
                          : t('staff.status_inactive')
                      }
                      color={data.isActive !== false ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
                <Box py={1}>
                  <Grid container spacing={2} justifyContent="space-between">
                    <Grid item md={7}>
                      <LoadingButton
                        color="primary"
                        disabled={false}
                        fullWidth
                        size="large"
                        variant="contained"
                        loading={userTransactionResult.isLoading}
                        onClick={onGetTransactionForUser}
                      >
                        {t('get_latest_transaction_btn')}
                      </LoadingButton>
                    </Grid>
                    <Grid item md={5}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={selectedDate}
                          onChange={(newValue) => {
                            setSelectedDate(newValue);
                          }}
                          slotProps={{ textField: { variant: 'outlined' } }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>
                  <Box mt={2}>
                    <LoadingButton
                      fullWidth
                      variant="outlined"
                      color="warning"
                      loading={resetLoading}
                      onClick={onSendPasswordReset}
                    >
                      {t('staff.send_password_reset')}
                    </LoadingButton>
                  </Box>
                  <Box mt={1}>
                    <LoadingButton
                      fullWidth
                      variant="outlined"
                      color={data.isActive !== false ? 'error' : 'success'}
                      loading={deactivateLoading || reactivateLoading}
                      onClick={onToggleActive}
                    >
                      {data.isActive !== false
                        ? t('staff.deactivate_account')
                        : t('staff.reactivate_account')}
                    </LoadingButton>
                  </Box>
                </Box>
              </CardContent>
              <Divider />
            </Card>
          </Grid>
          <Grid item lg={8} md={6} xs={12}>
            <form
              autoComplete="off"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!id) return;
                try {
                  await updateUser({ id, email: editEmail, cellNumber: editCellNumber }).unwrap();
                  dispatch(
                    openSnackbar({
                      open: true,
                      message: t('save_btn') + ' successful',
                      variant: 'alert',
                      alert: { color: 'success' },
                      close: false,
                    })
                  );
                } catch {
                  dispatch(
                    openSnackbar({
                      open: true,
                      message: t('errors.generic_error'),
                      variant: 'alert',
                      alert: { color: 'error' },
                      close: false,
                    })
                  );
                }
              }}
            >
              <Card>
                <CardHeader title={t('titles.profile')} />
                <Divider />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item md={6} xs={12}>
                      <TextField
                        fullWidth
                        helperText={t('validations_specify_surname')}
                        label={t('surname_label')}
                        name="surname"
                        disabled
                        value={data.lastName}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextField
                        fullWidth
                        label={t('firstname_label')}
                        name="middleName"
                        disabled
                        value={data.firstName}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <Grid container justifyContent="space-between">
                        <Grid item md={1} mr={4}>
                          <Select
                            labelId="country-code-label"
                            id="country-code-id"
                            value={country}
                            onChange={handleCountryCodeChange}
                          >
                            <MenuItem value={10}>+353</MenuItem>
                            <MenuItem value={40}>+243</MenuItem>
                            <MenuItem value={20}>+27</MenuItem>
                            <MenuItem value={30}>+44</MenuItem>
                          </Select>
                        </Grid>
                        <Grid item md={9}>
                          <TextField
                            fullWidth
                            label={t('tables.cell_number')}
                            name="cellNumber"
                            value={editCellNumber}
                            onChange={(e) => setEditCellNumber(e.target.value)}
                            variant="outlined"
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextField
                        fullWidth
                        label={t('email_label')}
                        name="email"
                        type="email"
                        value={editEmail}
                        onChange={(e) => setEditEmail(e.target.value)}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item md={12}></Grid>
                  </Grid>
                </CardContent>
                <Divider />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    p: 2,
                  }}
                >
                  <LoadingButton
                    type="submit"
                    color="primary"
                    variant="contained"
                    loading={saveLoading}
                  >
                    {t('save_btn')}
                  </LoadingButton>
                </Box>
              </Card>
            </form>
          </Grid>
          {userTransactionResult.data && (
            <Grid container>
              {userTransactionResult.data.incomes.length > 0 && (
                <Grid item md={12}>
                  <Typography variant="h3">{t('income_lbl') + getIncomesTotal() + '$'}</Typography>
                  <IncomeTransactionHistory
                    incomesTransactionResult={incomesColumnsData}
                    employee_name={data.names}
                  />
                </Grid>
              )}
              {userTransactionResult.data.expenses.length > 0 && (
                <Grid item md={12}>
                  <Typography variant="h3">
                    {t('expenses_lbl') + getExpensesTotal() + '$'}
                  </Typography>
                  <ExpenseTransactionHistory
                    expenseTransactionResult={expensesColumnsData}
                    employee_name={data.names}
                  />
                </Grid>
              )}
            </Grid>
          )}
          <Grid item xs={12}>
            <StaffActivityFeed userId={id || ''} userName={data.names} lastLogin={data.lastLogin} />
          </Grid>
        </Grid>
      ) : (
        <Box>{t('errors.generic_error')}</Box>
      )}
    </MainCard>
  );
};
export default UserDetailsPage;
